import { useCallback, useMemo } from 'react';
import {
    resolvePersonUserIdAsSelf,
    resolveWithKept,
    storedPersonLinks,
    type UserDocType,
    type AccountResolver,
    type CurrentPerson,
    type MatchableAccount,
    type StoredPersonLink
} from '@ast-data/shared';
import { useAuth } from '../auth/useAuth';
import { useActiveUsers } from './useActiveUsers';
import { useUserLabelState } from './useUserLabel';
import { useUserDirectory } from './userDirectory';

// The pure rules live in shared, where the server relinks by them too.
export {
    compatibleStoredLink,
    resolveKeepingStored,
    resolvePersonUserId,
    resolvePersonUserIds,
    resolvePersonUserIdAsSelf,
    storedPersonLinkList,
    storedPersonLinks,
    type CurrentPerson,
    type StoredPersonLink
} from '@ast-data/shared';

/**
 * The accounts a typed name may resolve to, each with the two names it answers
 * to: the app's own label and the Crewmeister spelling the office mapped. The
 * user list comes from the caller, who decides whether deactivated accounts
 * count. Worked out once by the user directory; this picks the caller's ones.
 */
export function useMatchableAccounts(
    users: readonly UserDocType[]
): MatchableAccount[] {
    const { accounts } = useUserDirectory();
    return useMemo(() => {
        const wanted = new Set(users.map((user) => user.id));
        return accounts.filter((account) => wanted.has(account.id));
    }, [users, accounts]);
}

/**
 * The name -> account resolver every form and the Zeiterfassung save path use:
 * the ACTIVE accounts first, then every account (buildTieredAccountResolver).
 *
 * Deactivating somebody takes them out of the pick lists but must not take them
 * out of records they are already named in. The `*Id` link is recomputed from
 * the name on EVERY save, so an active-only rule would blank the author of a
 * two-year-old Tagebuch the moment somebody corrects a typo in it — and with
 * the link goes the identity the double-booking check aggregates by.
 *
 * Built ONCE by the user directory: this hook sits behind every form dialog
 * and every grid column, and each used to build its own.
 */
export function useAccountResolver(): AccountResolver<MatchableAccount> {
    return useUserDirectory().resolver;
}

/**
 * The signed-in user as a person — the ONE source for "vorbelegt mit dem
 * aktuellen Benutzer", so that every such prefill carries an id.
 *
 * The name is the app's own label (`useUserLabel`: the custom name, else the
 * Synology username), taken from the synced user row where it has arrived and
 * from the token where it has not. The Synology `displayName` is deliberately
 * NOT used, though the token carries it: on bulk import it holds the account
 * DESCRIPTION rather than a name, so prefilling it wrote a name that matched no
 * account and the id link came out empty.
 */
export function useCurrentPerson(): CurrentPerson | null {
    const { user: authUser } = useAuth();
    const users = useActiveUsers();
    const { label, ready } = useUserLabelState();
    return useMemo(() => {
        // Nobody rather than a provisional name: forms prefill their author
        // from here and store it as text, so answering before the profiles have
        // arrived files the day under the Synology login name for good.
        if (!authUser || !ready) {
            return null;
        }
        const row = users.find((user) => user.id === authUser.id);
        // Without the row, the token still answers what the label rule needs:
        // an id to look the profile up by and a username to fall back on.
        const name = label(
            row ??
                ({
                    id: authUser.id,
                    username: authUser.username
                } as UserDocType)
        );
        return { id: authUser.id, name: name.trim() };
    }, [authUser, users, label, ready]);
}

/** Nothing stored yet — a constant, so a caller's hook deps stay stable. */
const NO_STORED_LINKS: readonly StoredPersonLink[] = [];

/**
 * The name→id resolver every form saves through — see the rule above.
 *
 * `stored` is what the record being edited already links, name by name, and it
 * WINS. A form recomputes every person link on every save, so without it,
 * saving an unrelated correction re-resolves names nobody touched — and a name
 * the rule cannot place any more comes back empty and drops the account. A name
 * stops being placeable when the account was renamed since, when two accounts
 * have come to share it, or when the link never came from the name at all (the
 * import's assignment table and the Zeitabgleich set one deliberately). Pass
 * the record's stored pairs when editing; leave it out for a new one.
 *
 * The consequence: a WRONG link cannot be corrected by picking the name again,
 * because the name is what keeps it. Change the name, or repair it in the
 * Zeitabgleich — which is also where a wrong one comes from.
 */
export function usePersonUserId(
    stored: readonly StoredPersonLink[] = NO_STORED_LINKS
): {
    resolve: (name: string) => string;
    resolveAll: (names: string[]) => string[];
} {
    const resolveAccount = useAccountResolver();
    const me = useCurrentPerson();
    const kept = useMemo(() => storedPersonLinks(stored), [stored]);

    const resolveByRule = useCallback(
        (name: string) => resolvePersonUserIdAsSelf(name, me, resolveAccount),
        [me, resolveAccount]
    );
    const resolveAll = useCallback(
        (names: string[]) =>
            resolveWithKept(names, kept, resolveByRule, () => false),
        [kept, resolveByRule]
    );
    const resolve = useCallback(
        (name: string) => resolveAll([name])[0],
        [resolveAll]
    );

    return { resolve, resolveAll };
}
